/**
 * Shared pure helpers + types for the budget detailverdeling designer.
 *
 * Both verdeling-views (label-first `BudgetSplitDesigner` canvas and the
 * kostenplaats-first `KostenplaatsVerdelingCanvas`) import these so the
 * formatting, color tones and percentage math stay identical and there is a
 * single source of truth. Keep this module side-effect free.
 */

import { InvestmentItemBudgetLabel } from "screens/investment/types/investment";
import { UnitDTO } from "shared/types/unit";

/** Currency formatter — whole euros, nl-BE locale (matches the rest of the UI). */
export const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

/** Stable-ish unique id for draft rows / groups / allocations. */
export const newDraftId = (prefix = "d") =>
  `${prefix}_${Math.random().toString(36).slice(2, 9)}_${Date.now()}`;

/** Tailwind tone per renovation-label type. Drives chip + row coloring. */
export const LABEL_TYPE_TONE: Record<string, string> = {
  esthetisch: "border-pink-200 bg-pink-50 text-pink-700",
  energetisch: "border-emerald-200 bg-emerald-50 text-emerald-700",
  veiligheid: "border-rose-200 bg-rose-50 text-rose-700",
  comfort: "border-blue-200 bg-blue-50 text-blue-700",
  technisch: "border-amber-200 bg-amber-50 text-amber-700",
  algemeen: "border-neutral-200 bg-neutral-50 text-neutral-700",
};

/** Net surface m² lookup, falling back to gross when net is missing. */
export const getUnitSurface = (u?: UnitDTO | null): number | null => {
  if (!u) return null;
  const v = (u.netSurface ?? u.grossSurface) as number | null | undefined;
  return typeof v === "number" && v > 0 ? Math.round(v) : null;
};

/** Equal-split percentages across unitIds. Last unit absorbs rounding. */
export const equalSplitPercentages = (
  unitIds: string[],
): Record<string, number> => {
  const out: Record<string, number> = {};
  if (unitIds.length === 0) return out;
  const each = 100 / unitIds.length;
  let assigned = 0;
  unitIds.forEach((id, idx) => {
    const v =
      idx === unitIds.length - 1
        ? Math.max(0, +(100 - assigned).toFixed(2))
        : +each.toFixed(2);
    out[id] = v;
    assigned += v;
  });
  return out;
};

/**
 * Payload for one investment line sent to the bucket `/split` endpoint.
 * Both verdeling-views build an array of these; the BE creates one
 * InvestmentItem per entry.
 */
export interface SplitItemPayload {
  labelId: number | null;
  amount: number;
  unitIds: string[];
  /** Optional per-unit % hint (only meaningful when unitIds.length > 1). */
  unitPercentages?: Record<string, number>;
  title: string;
  description?: string;
  budgetLabel: InvestmentItemBudgetLabel | null;
}

/** One unit's slice of the total draft, used by the per-kostenplaats overview. */
export interface UnitVerdelingRow {
  unitId: string;
  amount: number;
}

/** Per-kostenplaats rollup of a verdeling-draft (view-agnostic). */
export interface UnitVerdelingSummaryData {
  /** Per-unit totals, sorted by amount descending. */
  rows: UnitVerdelingRow[];
  /** € on lines that have no kostenplaats attached yet. */
  unassigned: number;
  /** Sum of all line amounts (assigned + unassigned). */
  total: number;
}

/**
 * Aggregate a list of split-payloads into a per-kostenplaats (unit) rollup.
 *
 * Works identically for both verdeling-views since both produce
 * `SplitItemPayload[]`:
 *  - no units      → counted as `unassigned`
 *  - one unit      → full amount on that unit
 *  - several units → split by `unitPercentages` when complete, else equal
 *
 * Pure + side-effect free so it can be unit-tested in isolation.
 */
export const aggregatePerUnit = (
  items: SplitItemPayload[],
): UnitVerdelingSummaryData => {
  const perUnit = new Map<string, number>();
  let unassigned = 0;
  let total = 0;

  const add = (unitId: string, amount: number) =>
    perUnit.set(unitId, (perUnit.get(unitId) || 0) + amount);

  for (const it of items) {
    const amt = Number(it.amount) || 0;
    if (amt <= 0) continue;
    total += amt;

    const unitIds = it.unitIds ?? [];
    if (unitIds.length === 0) {
      unassigned += amt;
      continue;
    }
    if (unitIds.length === 1) {
      add(unitIds[0], amt);
      continue;
    }

    const pcts = it.unitPercentages;
    const hasFullPct =
      !!pcts && unitIds.every((u) => typeof pcts[u] === "number");
    if (hasFullPct) {
      unitIds.forEach((u) => add(u, (amt * (pcts![u] || 0)) / 100));
    } else {
      const each = amt / unitIds.length;
      unitIds.forEach((u) => add(u, each));
    }
  }

  const rows = Array.from(perUnit.entries())
    .map(([unitId, amount]) => ({ unitId, amount: Math.round(amount) }))
    .sort((a, b) => b.amount - a.amount);

  return { rows, unassigned: Math.round(unassigned), total: Math.round(total) };
};
